<template>
    <div data-cy="wing-toppings" v-if="hasAvailableFlavors">
        <ion-card-title class="mx-8 mb-8 ion-text-center bold">{{ 'Choose Your ' + (initialItem?.isAppetizer ? 'Sauce Cup' : 'Flavor') }}</ion-card-title>
        <div>
            <div v-if="!isCartLoader || isLoader">
                <radiolist key="SauceOrFlavor" :options="availableTopping?.flavors" keyName="name" dense v-model="localFlavor" data-cy="choose-flavor" />
            </div>
            <div v-else>
                <dynamicSkeleText element="div" :childElements="[{ width: 60, height: '24px', class: 'mx-a mb-8' }]" />
                <dynamicSkeleText v-for="i in Array.from({ length: 4 }, (_, i) => i + 1)" :key="i" element="div" :childElements="skeleText" />
            </div>
        </div>

        <ion-item-divider class="my-8" v-if="hasAvailableDressings" />

        <ion-card-title class="mx-8 mb-8 ion-text-center bold" v-if="hasAvailableDressings">Sauce Cup?</ion-card-title>
        <div>
            <div v-if="!isCartLoader || isLoader">
                <radioGroup dense forceReselect key="SauceCup" keyName="name" componentKey="SauceCup" v-model="dressing1" :options="availableTopping?.dressings"></radioGroup>
            </div>
            <div v-else>
                <dynamicSkeleText element="div" :childElements="[{ width: 60, height: '24px', class: 'mx-a mb-8' }]" />
                <dynamicSkeleText v-for="i in Array.from({ length: 4 }, (_, i) => i + 1)" :key="i" element="div" :childElements="skeleText" />
            </div>
        </div>

        <div v-if="selectedSize?.isPartySize && !initialItem?.isAppetizer">
            <ion-card-title class="mx-8 mb-8 ion-text-center bold">Second Sauce Cup?</ion-card-title>
            <div>
                <div v-if="!isCartLoader || isLoader">
                    <radioGroup dense forceReselect keyName="name" key="SecondSauceCup" componentKey="SecondSauceCup" v-model="dressing2" :options="availableTopping?.dressings"></radioGroup>
                </div>
                <div v-else>
                    <dynamicSkeleText element="div" :childElements="[{ width: 60, height: '24px', class: 'mx-a mb-8' }]" />
                    <dynamicSkeleText v-for="i in Array.from({ length: 4 }, (_, i) => i + 1)" :key="i" element="div" :childElements="skeleText" />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, watch, computed, ref, onMounted } from 'vue';
import { IonCardTitle, IonItemDivider } from '@ionic/vue';
import radiolist from '@/components/inputs/radiolist.vue';
import baseToppings, { passThroughBindings } from '@/components/menu/toppings/baseToppings.vue';
import radioGroup from '@/components/inputs/radioGroup.vue';
import dynamicSkeleText from '@/components/dynamicSkeleText.vue';
import { skeleText } from '@/models/props/skeleText';
import { useStore } from 'vuex';

export default defineComponent({
    extends: baseToppings,
    components: { IonCardTitle, IonItemDivider, radiolist, radioGroup, dynamicSkeleText },
    setup(props, { emit }) {
        const store = useStore();
        const { localFlavor } = passThroughBindings(props, emit);
        const skeleText: skeleText[] = [
            { width: 5, height: '24px', display: 'inline-block', marginRight: '5px' },
            { width: 30, height: '24px', display: 'inline-block', marginRight: '5px' },
            { width: 20, height: '24px', display: 'inline-block', marginRight: '5px', background: 'transparent' },
            { width: 35, height: '24px', display: 'inline-block', marginRight: '5px' },
        ];
        const hasAvailableFlavors = computed(() => props.availableTopping?.flavors && props.availableTopping.flavors.length > 0);
        const hasAvailableDressings = computed(() => props.availableTopping?.dressings && props.availableTopping?.dressings.length > 0);
        const isLoader = computed(() => store.getters.getFlavorLoader);
        const isCartLoader = computed(() => store.getters.getCartEditLoader);
        const [d1, d2] = Array.isArray(props.dressing) ? props.dressing : [null, null];
        const dressing1 = ref(d1);
        const dressing2 = ref(d2);

        const initialDressings = computed(() => {
            const isPartySize = props.selectedSize?.isPartySize;
            const availableDressings = props.availableTopping?.dressings;

            const noDressing1 = availableDressings?.find((d) => d.name.toLowerCase().includes('no ')) || null;
            const noDressing2 = isPartySize ? noDressing1 : null;
            return [dressing1.value || noDressing1, isPartySize ? dressing2.value || noDressing2 : null];
        });

        watch(
            () => isLoader.value,
            (val) => {
                //@ts-ignore
                if (val == true) {
                    store.dispatch('cartEditLoader', false);
                }
            }
        );

        watch(
            () => initialDressings.value,
            (newDressings) => {
                [dressing1.value, dressing2.value] = newDressings;
            }
        );

        watch([() => dressing2.value, () => dressing1.value], ([d2, d1]) => {
            const isPartySize = props.selectedSize?.isPartySize;
            if ((d1 && d2) || d1) {
                emit('update:dressing', isPartySize ? [d1, d2] : d1);
            }
        });

        watch(
            () => props.availableTopping,
            (update) => {
                setTimeout(() => {
                    localFlavor.value = update?.flavors?.find((f) => f.toppingPid === localFlavor.value?.toppingPid);
                });
                dressing1.value = update?.dressings?.find((f) => f.toppingPid === dressing1.value?.toppingPid);
            }
        );

        emit('blockSubmit', true);
        watch(localFlavor, () => emit('blockSubmit', false));

        // set default flavor for appetizers
        if (props.initialItem?.isAppetizer) {
            nextTick(() => {
                localFlavor.value = localFlavor.value ?? props.availableTopping?.flavors[0];
                emit('blockSubmit', false);
            });
        }

        onMounted(() => {
            nextTick(() => {
                [dressing1.value, dressing2.value] = initialDressings.value;
            });
        });

        return { dressing1, dressing2, localFlavor, hasAvailableDressings, hasAvailableFlavors, skeleText, isLoader, isCartLoader };
    },
});
</script>
